/* eslint-disable react/display-name */
import { Button } from '@chakra-ui/react';
import {
    faArrowLeft,
    faEllipsisVertical,
    faEllipsisH,
} from '@fortawesome/pro-regular-svg-icons';
import {
    CCloseIcon,
    CDownloadIcon,
    CFAIcon,
    CPencilIcon,
    CPlusIcon,
    CSaveIcon,
    CTrashIcon,
} from 'app/koddi-components/src/_ChakraComponents/Icons';
import { forwardRef, MouseEventHandler, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
    BorderRadius,
    ChakraSizes,
    Colors,
    ColorSchemes,
} from '../ChakraTheme';

import { useChakraTheme } from '../ChakraTheme/useChakraTheme.hooks';
import { ButtonBaseProps, LinkButtonProps } from './Button.type';

const ButtonBase = ({
    route,
    testId,
    onClick,
    innerRef,
    size,
    ...props
}: ButtonBaseProps): JSX.Element => {
    const history = useHistory();
    const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
        (event) => {
            if (route) {
                history?.push(route);
            }
            onClick?.(event);
        },
        [onClick, history, route]
    );
    const isSmall = [ChakraSizes.xs, ChakraSizes.sm].includes(
        size as ChakraSizes
    );
    return (
        <Button
            borderRadius={isSmall ? BorderRadius.SM : undefined}
            size={size}
            {...props}
            onClick={handleClick}
            data-test={`${testId}`}
            ref={innerRef}
        />
    );
};

export const ButtonRefBase = forwardRef<HTMLButtonElement, ButtonBaseProps>(
    ({ ...props }: ButtonBaseProps, ref) => {
        return <ButtonBase {...props} innerRef={ref} />;
    }
);

export const CButtonWrapper = ({
    children,
    ...props
}: ButtonBaseProps): JSX.Element => (
    <ButtonBase {...props} variant="wrapper">
        {children}
    </ButtonBase>
);

export const CTextButton = ({
    children,
    ...props
}: ButtonBaseProps): JSX.Element => {
    return (
        <ButtonBase {...props} variant="link">
            {children}
        </ButtonBase>
    );
};

export const CLinkButton = ({
    children,
    ...props
}: LinkButtonProps): JSX.Element => {
    return (
        <ButtonBase
            {...props}
            variant="link"
            as={props?.href ? 'a' : undefined}
        >
            {children}
        </ButtonBase>
    );
};

export const CPrimaryButton = ({
    children,
    ...props
}: ButtonBaseProps): JSX.Element => {
    return (
        <ButtonBase {...props} variant="primary">
            {children}
        </ButtonBase>
    );
};

export const CSecondaryButton = ({
    children,
    ...props
}: ButtonBaseProps): JSX.Element => {
    return (
        <ButtonBase {...props} variant="secondary">
            {children}
        </ButtonBase>
    );
};

export const CBorderButton = ({
    children,
    ...props
}: ButtonBaseProps): JSX.Element => {
    return (
        <ButtonBase {...props} variant="border">
            {children}
        </ButtonBase>
    );
};

export const CSubtleButton = ({
    children,
    ...props
}: ButtonBaseProps): JSX.Element => {
    return (
        <ButtonBase
            {...props}
            variant="subtle"
            borderColor={Colors.grayLight}
            borderWidth={1}
        >
            {children}
        </ButtonBase>
    );
};

export const CGhostButton = ({
    children,
    ...props
}: ButtonBaseProps): JSX.Element => {
    return (
        <ButtonBase {...props} variant="ghost">
            {children}
        </ButtonBase>
    );
};

export const CMonochromaticButton = ({
    children,
    color,
    ...props
}: Omit<ButtonBaseProps, 'color'> & { color: ColorSchemes }): JSX.Element => {
    const theme = useChakraTheme();
    const colorMap = theme.colors[color] ?? theme.colors.gray;
    return (
        <ButtonBase {...props} color={colorMap[50]} bg={colorMap[500]}>
            {children}
        </ButtonBase>
    );
};

export const CIconButton = ({
    children,
    ...props
}: ButtonBaseProps): JSX.Element => {
    return (
        <ButtonBase {...props} variant="icon">
            {children}
        </ButtonBase>
    );
};

export const CEditButton = ({ ...props }: ButtonBaseProps): JSX.Element => (
    <CIconButton {...props}>
        <CPencilIcon height="80%" />
    </CIconButton>
);

export const CDeleteButton = ({ ...props }: ButtonBaseProps): JSX.Element => (
    <CIconButton {...props}>
        <CTrashIcon height="75%" />
    </CIconButton>
);

export const CSaveButton = ({ ...props }: ButtonBaseProps): JSX.Element => (
    <CIconButton {...props}>
        <CSaveIcon />
    </CIconButton>
);

export const CDownloadButton = ({ ...props }: ButtonBaseProps): JSX.Element => (
    <CIconButton {...props}>
        <CDownloadIcon />
    </CIconButton>
);

export const CPlusButton = ({ ...props }: ButtonBaseProps): JSX.Element => (
    <ButtonBase {...props}>
        <CPlusIcon fill={Colors.white} />
    </ButtonBase>
);

export const CStackedButton = ({
    children,
    ...props
}: ButtonBaseProps): JSX.Element => {
    return (
        <ButtonBase {...props} variant="stacked">
            {children}
        </ButtonBase>
    );
};

export const COutlineButton = ({
    children,
    ...props
}: ButtonBaseProps): JSX.Element => {
    return (
        <ButtonBase {...props} variant="outline">
            {children}
        </ButtonBase>
    );
};

export const CSuccessButton = ({ ...props }: ButtonBaseProps): JSX.Element => (
    <CBorderButton
        {...props}
        borderColor={Colors.success}
        color={Colors.success}
        fill={Colors.success}
        _hover={{
            borderColor: Colors.successDark,
            color: Colors.successDark,
            backgroundColor: Colors.successLight,
        }}
    />
);

export const CErrorButton = ({ ...props }: ButtonBaseProps): JSX.Element => (
    <CBorderButton
        {...props}
        borderColor={Colors.error}
        color={Colors.error}
        fill={Colors.error}
        _hover={{
            borderColor: Colors.errorDark,
            color: Colors.errorDark,
            backgroundColor: Colors.errorLight,
        }}
    />
);

export const CErrorPrimaryButton = ({
    ...props
}: ButtonBaseProps): JSX.Element => (
    <ButtonBase
        {...props}
        variant="primary"
        backgroundColor={Colors.error}
        color={Colors.white}
        borderColor={Colors.error}
        _hover={{
            backgroundColor: Colors.errorDark,
            borderColor: Colors.errorDark,
        }}
        _active={{
            backgroundColor: Colors.errorDark,
            borderColor: Colors.errorDark,
        }}
    />
);

export const CBackButton = ({ ...props }: ButtonBaseProps): JSX.Element => (
    <CIconButton {...props}>
        <CFAIcon icon={faArrowLeft} />
    </CIconButton>
);

export const CCloseButton = ({ ...props }: ButtonBaseProps): JSX.Element => (
    <CIconButton {...props}>
        <CCloseIcon height="75%" />
    </CIconButton>
);

export const CElipsisButton = forwardRef<
    HTMLButtonElement,
    ButtonBaseProps & {
        direction?: 'vertical' | 'horizontal';
    }
>(({ direction = 'vertical', ...props }, ref) => {
    return (
        <ButtonBase {...props} innerRef={ref} variant="icon">
            <CFAIcon
                icon={
                    direction === 'vertical' ? faEllipsisVertical : faEllipsisH
                }
            />
        </ButtonBase>
    );
});
